import React from 'react'
import clsx from 'clsx'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'

import {
  EmptyStateAction,
  EmptyStateImage,
  EmptyStateWithIllustration,
} from 'components/EmptyState'
import useSETrackEvent from 'app/SearchPage/hooks/useSETrackEvent'
import { getErrorType } from 'app/SearchPage/utils/error'
import {
  NotFoundError,
  NotFoundWithFiltersError,
  InternalServerError,
  NetworkError,
} from 'app/SearchPage/utils/customError'
import ImgErrorNoConnection from 'public/images/errors/img-no-connection.svg'
import { useContentSearchFilter } from 'stores/content-search'

interface ICustomErrorBoundaryProps {
  children: React.ReactNode
  onReset: (error: Error) => any
  onError: (error: Error) => any
  resetKeys: string[]
}
const CustomErrorBoundary = ({
  resetKeys,
  onReset,
  onError,
  children,
}: ICustomErrorBoundaryProps) => {
  return (
    <ErrorBoundary
      resetKeys={resetKeys}
      FallbackComponent={CustomErrorFallback}
      onReset={onReset}
      onError={onError}
    >
      {children}
    </ErrorBoundary>
  )
}

export function CustomErrorFallback({
  error,
  resetErrorBoundary,
}: FallbackProps) {
  const { SETrackSearchResultsPage } = useSETrackEvent()
  const errorType = getErrorType(error)

  if (
    ![
      'NotFoundError',
      'NotFoundWithFiltersError',
      'InternalServerError',
      'NetworkError',
    ].includes(errorType)
  ) {
    throw error
  }

  // @ts-expect-error error is not an Error object
  const { title, description } = error
  const contentSearchFilterStore = useContentSearchFilter()

  return (
    <div
      className={clsx(
        'px-12 py-8 border-t border-subdued flex items-center mt-[6px] h-full',
        contentSearchFilterStore.isShowQuickFilters && 'pt-16',
        !contentSearchFilterStore.isShowQuickFilters && 'h-[calc(100vh-118px)]'
      )}
    >
      <EmptyStateWithIllustration
        title={title}
        description={description}
        fullscreen={false}
        className="w-full text-center"
      >
        {error instanceof NotFoundError && (
          <>
            <EmptyStateImage
              data-testid="error-boundary-image"
              imageSrc="/images/errors/img-empty-search-result.png"
            />
            <EmptyStateAction
              data-testid="error-boundary-action-primary"
              size="lg"
              className="w-full mt-2 mb-2"
              onClick={() => {
                SETrackSearchResultsPage('CLICK_CHANGE_SEARCH', {
                  event_trigger: 'ERROR_NO_RESULTS',
                })
                resetErrorBoundary('NOT_FOUND_ERROR')
              }}
            >
              Ganti pencarian
            </EmptyStateAction>
            <EmptyStateAction
              data-testid="error-boundary-action-secondary"
              color="white"
              size="lg"
              onClick={() => {
                SETrackSearchResultsPage('CLICK_HELPDESK_BUTTON', {
                  event_trigger: 'ERROR_NO_RESULTS',
                })
                window.location.href =
                  process.env.NEXT_PUBLIC_PUSAT_INFORMASI_RUMDIK_BASE_URL +
                  '/hc/id/articles/52513043812761-Kendala-Fitur-Pencarian'
              }}
            >
              Cek Pusat Bantuan
            </EmptyStateAction>
          </>
        )}

        {error instanceof NotFoundWithFiltersError && (
          <>
            <EmptyStateImage
              data-testid="error-boundary-image"
              imageSrc="/images/errors/img-unmatched-filter-document.png"
            />
            <EmptyStateAction
              data-testid="error-boundary-action-primary"
              size="lg"
              className="w-full mt-2"
              onClick={() => {
                SETrackSearchResultsPage('CLICK_EDIT_CATEGORY', {
                  event_trigger: 'ERROR_NO_RESULTS_FILTER',
                })
                resetErrorBoundary('NOT_FOUND_WITH_FILTERS_ERROR')
              }}
            >
              Edit Kategori
            </EmptyStateAction>
          </>
        )}

        {error instanceof InternalServerError && (
          <>
            <EmptyStateImage
              data-testid="error-boundary-image"
              imageSrc="/images/errors/img-error-fixing.png"
            />
            <EmptyStateAction
              data-testid="error-boundary-action-primary"
              size="lg"
              className="w-full mt-2"
              onClick={() => {
                SETrackSearchResultsPage('CLICK_TRY_AGAIN', {
                  event_trigger: 'ERROR_SYSTEM_ERROR',
                })
                resetErrorBoundary('INTERNAL_SERVER_ERROR')
              }}
            >
              Coba lagi
            </EmptyStateAction>
          </>
        )}

        {error instanceof NetworkError && (
          <>
            <ImgErrorNoConnection
              data-testid="error-boundary-image"
              as="EmptyStateImageSvg"
            />
            <EmptyStateAction
              data-testid="error-boundary-action-primary"
              size="lg"
              className="w-full mt-2"
              onClick={() => {
                SETrackSearchResultsPage('CLICK_TRY_AGAIN', {
                  event_trigger: 'ERROR_NO_INTERNET',
                })
                resetErrorBoundary('NETWORK_ERROR')
              }}
            >
              Coba lagi
            </EmptyStateAction>
          </>
        )}
      </EmptyStateWithIllustration>
    </div>
  )
}

export default CustomErrorBoundary
